import React, { useMemo } from 'react';
import { IngredientPurchaseRecord } from '../../../../types';
import './CategoryBreakdown.scss';

interface CategoryBreakdownProps {
  purchases: IngredientPurchaseRecord[];
}

const formatCurrency = (value: number) =>
  new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP' }).format(Math.round(value));

const CategoryBreakdown: React.FC<CategoryBreakdownProps> = ({ purchases }) => {
  const categoryData = useMemo(() => {
    const map = new Map<string, { total: number; count: number }>();

    purchases.forEach(p => {
      const name = p.expense_category_name || 'Sin categoría';
      const existing = map.get(name) || { total: 0, count: 0 };
      map.set(name, {
        total: existing.total + p.total_neto,
        count: existing.count + 1,
      });
    });

    const sorted = Array.from(map.entries())
      .map(([name, data]) => ({ name, ...data }))
      .sort((a, b) => b.total - a.total);

    const grandTotal = sorted.reduce((sum, item) => sum + item.total, 0);

    return sorted.map(item => ({
      ...item,
      percentage: grandTotal > 0 ? (item.total / grandTotal) * 100 : 0,
    }));
  }, [purchases]);

  if (categoryData.length === 0) return null;

  const maxTotal = categoryData[0]?.total || 1;

  return (
    <div className="category-breakdown-card">
      <div className="card-header">
        <h3 className="card-title">Distribución por Categoría</h3>
        <span className="card-subtitle">{categoryData.length} categorías</span>
      </div>
      <div className="card-body">
        {categoryData.map((item, index) => (
          <div key={item.name} className="category-row">
            <div className="category-info">
              <span className="category-rank">{index + 1}</span>
              <span className="category-name">{item.name}</span>
              <span className="category-count">{item.count}x</span>
            </div>
            <div className="category-bar-container">
              <div
                className="category-bar"
                style={{ width: `${(item.total / maxTotal) * 100}%` }}
              />
            </div>
            <div className="category-values">
              <span className="category-total">{formatCurrency(item.total)}</span>
              <span className="category-percentage">{item.percentage.toFixed(1)}%</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default CategoryBreakdown;
